import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { v4 as uuidv4 } from "uuid";

import {
	useGetCampaignStatsQuery,
	useUpdateListMutation,
} from "@/pages/Private/redux/list/list.api";

import { UpdateType } from "@/enum/list.enum";

import { showGlobalToast } from "@/utils/showGlobalToast";

import { CampaignLockLeadStates, resolveCampaignLockLeadStates } from "./campaignLockLeadStates";
import {
	buildClonedCampaignConfig,
	CLONED_CAMPAIGN_NAME_SUFFIX,
} from "./buildClonedCampaignConfig.util";

/** The minimum a campaign entry needs for the explainer to duplicate it. */
interface LockedCampaign {
	id: string;
	name: string;
}

export interface CampaignLockExplainer {
	isOpened: boolean;
	leadStates: CampaignLockLeadStates;
	isDuplicating: boolean;
	open: () => void;
	close: () => void;
	duplicate: () => Promise<void>;
}

/**
 * State and actions behind the locked-campaign explainer (PREC-3786): the lead spread it shows and
 * the duplicate that gets the user an editable sequence again.
 *
 * The lead counts come from the per-campaign stats the list already serves, so nothing new is asked
 * of the API — and the request is skipped entirely unless the open campaign is actually locked.
 */
export const useCampaignLockExplainer = ({
	listId,
	campaignUuid,
	campaignConfig,
	isLocked,
	targetStopDate,
}: {
	listId?: number;
	campaignUuid: string;
	campaignConfig: Record<string, LockedCampaign> | null | undefined;
	isLocked: boolean;
	/** The end date the copy starts with (`eventStopDatePrefill`), never the source's own. */
	targetStopDate?: string | null;
}): CampaignLockExplainer => {
	const { t } = useTranslation();
	const [isOpened, setIsOpened] = useState<boolean>(false);
	const [updateList, { isLoading: isDuplicating }] = useUpdateListMutation();

	const { data: campaignStats } = useGetCampaignStatsQuery(
		{ listId: Number(listId) },
		{ skip: !listId || !isLocked }
	);

	const leadStates = useMemo(
		() =>
			resolveCampaignLockLeadStates(
				campaignStats?.data?.find((stat) => stat.campaignId === campaignUuid)
			),
		[campaignStats, campaignUuid]
	);

	/**
	 * Copy the sequence into a fresh, unlocked campaign. The copy carries no senders (so it starts
	 * invalid) and no leads — the modal says both, because silently doing either would be worse than
	 * the lock itself.
	 */
	const duplicate = useCallback(async (): Promise<void> => {
		const source = campaignConfig?.[campaignUuid];

		if (!source || !listId) {
			return;
		}

		const newCampaignUuid = uuidv4();

		try {
			await updateList({
				id: listId,
				updateType: UpdateType.CAMPAIGN_CONFIG,
				campaignConfig: JSON.stringify(
					buildClonedCampaignConfig(campaignConfig, source, newCampaignUuid, targetStopDate)
				),
				campaignIdToUpdate: newCampaignUuid,
				campaignWriteOrigin: "duplicate-locked",
			}).unwrap();

			setIsOpened(false);
			showGlobalToast(
				t("campaignLockExplainer.duplicateSuccess", {
					name: `${source.name} ${CLONED_CAMPAIGN_NAME_SUFFIX}`,
				}),
				6000,
				"success"
			);
		} catch {
			showGlobalToast(t("campaignLockExplainer.duplicateError"));
		}
	}, [campaignConfig, campaignUuid, listId, t, targetStopDate, updateList]);

	// `open` and `close` must keep their identity across renders. `open` is handed to the campaign
	// builder as `onLockedStructureEdit`, which the builder puts in the dependency list of the memo
	// holding the campaign's nodes — a fresh function there invalidates that memo on every parent
	// render, and the builder's effect then re-seeds its live canvas from the last saved payload,
	// silently deleting the step the user just added (PREC-3786 regression).
	const open = useCallback(() => setIsOpened(true), []);
	const close = useCallback(() => setIsOpened(false), []);

	return { isOpened, leadStates, isDuplicating, open, close, duplicate };
};
